/**
 * 원본 주소(`<scheme>://<container>/<prefix>`)의 검증과 정규화.
 *
 * 서버가 정본이고(`spnp` `service/dataset/source.normalize_source_ref` + `wire.type.ObjectUri`)
 * FE 는 **같은 규칙을 제출 전에 먼저 본다** — 250ms 왕복을 오타 한 번에 쓰지 않기 위해서다.
 * 서버가 그래도 `400 dataset.source_invalid` 를 내면 그 응답이 이긴다.
 *
 * Owns:
 * - 주소 문법 판정, 정규화 결과, 실패 사유의 사용자 문구, 표시 이름 기본값 추출.
 *
 * Does not own:
 * - 등록 요청 — `api/datasetApi.ts`. 폼 상태 — `page/components/RegisterDatasetModal.tsx`.
 */

/** 운영(`az`)과 dev 미러(`file`). 같은 key 규칙이라 1:1 로 대응한다 — 배포 환경이지 원본 종류가 아니다. */
export const SOURCE_REF_SCHEMES = ["az", "file"] as const;

// 서버 `SOURCE_REF_MAX_LENGTH`.
const MAX_LENGTH = 1024;
const SCHEME_SEPARATOR = "://";

export type SourceRefCheck =
  { ok: true; normalized: string; lastSegment: string } | { ok: false; message: string };

/**
 * 주소를 검증하고 서버가 저장할 문자열로 정규화한다.
 *
 * 정규화는 양끝·중복 `/` 를 흡수하고 빈 세그먼트를 접는다. `..` 세그먼트와 역슬래시는 거부한다.
 */
export function checkSourceRef(raw: string): SourceRefCheck {
  const text = raw.trim();
  if (text === "") {
    return { ok: false, message: "원본 주소를 입력해 주세요." };
  }

  const separatorAt = text.indexOf(SCHEME_SEPARATOR);
  if (separatorAt <= 0) {
    return {
      ok: false,
      message: "주소는 `az://컨테이너/경로` 형식이어야 합니다.",
    };
  }

  const scheme = text.slice(0, separatorAt);
  if (!isSupportedScheme(scheme)) {
    return {
      ok: false,
      message: `지원하는 주소 종류는 ${SOURCE_REF_SCHEMES.join(" · ")} 입니다.`,
    };
  }

  const rest = text.slice(separatorAt + SCHEME_SEPARATOR.length);
  const slashAt = rest.indexOf("/");
  const container = slashAt === -1 ? rest : rest.slice(0, slashAt);
  const prefix = slashAt === -1 ? "" : rest.slice(slashAt + 1);
  if (container.trim() === "") {
    return { ok: false, message: "주소에 컨테이너 이름이 필요합니다." };
  }

  const prefixCheck = normalizePrefix(prefix);
  if (!prefixCheck.ok) {
    return prefixCheck;
  }

  const normalized = `${scheme}${SCHEME_SEPARATOR}${container.trim()}/${prefixCheck.value}`;
  if (normalized.length > MAX_LENGTH) {
    return {
      ok: false,
      message: `주소는 ${MAX_LENGTH}자까지 입력할 수 있습니다.`,
    };
  }

  const segments = prefixCheck.value.split("/");
  return { ok: true, normalized, lastSegment: segments[segments.length - 1] };
}

/** 표시 이름의 기본값 — 주소의 마지막 세그먼트. 판정할 수 없으면 빈 문자열이다. */
export function suggestDisplayName(raw: string): string {
  const check = checkSourceRef(raw);
  return check.ok ? check.lastSegment : "";
}

function isSupportedScheme(value: string): boolean {
  return (SOURCE_REF_SCHEMES as readonly string[]).includes(value);
}

type PrefixCheck = { ok: true; value: string } | { ok: false; message: string };

function normalizePrefix(raw: string): PrefixCheck {
  const value = raw.trim();
  if (value === "") {
    return { ok: false, message: "주소에 컨테이너 안의 경로가 필요합니다." };
  }
  if (value.includes("\\")) {
    return { ok: false, message: "주소에 역슬래시(\\)를 쓸 수 없습니다." };
  }

  const segments = value.split("/").filter((segment) => segment !== "");
  if (segments.length === 0) {
    return { ok: false, message: "주소에 컨테이너 안의 경로가 필요합니다." };
  }
  if (segments.includes("..")) {
    return { ok: false, message: "주소에 `..` 세그먼트를 쓸 수 없습니다." };
  }

  return { ok: true, value: segments.join("/") };
}
